import { useState, useEffect, useCallback, useRef } from "react";
import UserIdentitiesService from "@/services/UserIdentitiesService";
import { UserIdentityFlowAction } from "@/services/UserIdentitiesService";
import type { UserIdentityStatusDto } from "@/services/UserIdentitiesService";
import { mapAnalyzeDocumentResponseToProcessingResult } from "@/screen/Identities/identityDocumentAnalysisMapper";
import { resolveEffectiveFlowAction } from "@/screen/Identities/IdentityForm/identityFlowNormalizer";
import { orchestratorErrorsToSingleMessage, extractWarnings } from "@/screen/Identities/IdentityForm/identityServerErrors";
import { resolveErrorMessage } from "@/screen/Identities/IdentityForm/identityErrorMessages";
import type { DocumentProcessingResult, FormMode } from "@/screen/Identities/Interfaces/IdentityFormInterfaces";

// Fase única de verdad - Protocolo de controlador orquestador
//
// La fase gobierna qué muestra IdentityDocumentUpload en cada momento y también
// le dice a IdentityForm qué componente renderizar.
//
//  idle              → archivos no seleccionados / esperando "Procesar documento"
//                      También es el estado de recuperación ante cualquier error:
//                      el usuario puede volver a intentarlo libremente.
//  uploading         → analyzeDocument en curso
//  dni_required      → análisis OK pero sin DNI extraído, usuario debe introducirlo
//                      Si falla el analyzeStatus, vuelve aquí para que el usuario reintente.
//  analyzing_status  → analyzeIdentityStatus en curso
//  flow_resolved     → analyzeIdentityStatus completado, acción efectiva conocida

export type DocumentUploadPhase = | "idle" | "uploading" | "dni_required" | "analyzing_status" | "flow_resolved";

export interface DocumentUploadState
{
    idFrontFile: File | null;
    idBackFile: File | null;
    idFrontPreview: string | null;
    idBackPreview: string | null;
    manualDniValue: string;
    manualDniError: string;
    phase: DocumentUploadPhase;
    /** Mensaje de error o advertencia para mostrar */
    statusMessage: string;
    /** Acción efectiva resuelta tras analyzeIdentityStatus */
    resolvedAction: UserIdentityFlowAction | null;
    /** Datos del status del interviniente para contexto en los componentes de flujo */
    identityStatus: UserIdentityStatusDto | null;
}

export interface UseDocumentUploadCallbacks
{
    /** Llamado con los datos extraídos del documento (parciales o completos) */
    onDocumentProcessed: (result: DocumentProcessingResult, frontPreview: string, backPreview: string) => void;
    /** Llamado con warning no bloqueante del servidor */
    onServerWarning: (message: string) => void;
    /** Llamado cuando hay un error bloqueante */
    onError: (message: string) => void;
    /** Llamado cuando el DNI se introduce manualmente — actualiza formData en el controller */
    onDniConfirmed: (dni: string) => void;
}

export interface UseDocumentUploadReturn
{
    state: DocumentUploadState;
    handleDocumentChange: (file: File | null, side: "front" | "back") => void;
    handleProcessDocument: () => Promise<void>;
    handleManualDniChange: (value: string) => void;
    handleManualDniConfirm: () => Promise<void>;
}

const INITIAL_STATE: DocumentUploadState =
{
    idFrontFile: null,
    idBackFile: null,
    idFrontPreview: null,
    idBackPreview: null,
    manualDniValue: "",
    manualDniError: "",
    phase: "idle",
    statusMessage: "",
    resolvedAction: null,
    identityStatus: null,
};

async function runAnalyzeStatus(dni: string): Promise<{action: UserIdentityFlowAction | null; status: UserIdentityStatusDto | null; warning: string; error: string}>
{
    const normalizedDni = (dni ?? "").trim().toUpperCase();
    if(!normalizedDni)
    {
        return {action: null, status: null, warning: "", error: resolveErrorMessage("INVALID_RESPONSE", "No se pudo verificar el estado del interviniente.")};
    }
    try
    {
        const res = await UserIdentitiesService.analyzeIdentityStatus(normalizedDni);
        const warnings = extractWarnings(res.errors);
        const warning = warnings.length > 0 ? orchestratorErrorsToSingleMessage(warnings) : "";

        const data = res.data;
        const hasValidStatus = !!data && typeof data === "object" && "action" in data;
        if(!res.success || !hasValidStatus)
        {
            const errorMsg = orchestratorErrorsToSingleMessage(res.errors) || resolveErrorMessage("INVALID_RESPONSE", "No se pudo verificar el estado del interviniente.");
            return {action: null, status: null, warning, error: errorMsg};
        }

        const status = data as UserIdentityStatusDto;
        const action = resolveEffectiveFlowAction(res.flow, status.action);
        return {action, status, warning, error: ""};
    }
    catch
    {
        return{
            action: null,
            status: null,
            warning: "",
            error: resolveErrorMessage("NETWORK_ERROR", "Error al verificar el estado del interviniente. Inténtalo de nuevo."),
        };
    }
}

function hasStoredIdDocument(value: string | null | undefined): boolean
{
    return typeof value === "string" && value.trim() !== "";
}

export function useDocumentUpload(mode: FormMode, userIdentityId: number | undefined, initialFrontPreview: string | null | undefined, initialBackPreview: string | null | undefined, initialIdDocument: string | null | undefined, callbacks: UseDocumentUploadCallbacks, canRenewIdentityDocument?: boolean, hasStoredDocuments?: boolean): UseDocumentUploadReturn
{
    const [state, setState] = useState<DocumentUploadState>(INITIAL_STATE);

    // Ref estable para los callbacks: evita cerrar sobre versiones viejas sin necesitar
    // que los callbacks sean dependencias de los handlers.
    const callbacksRef = useRef(callbacks);
    callbacksRef.current = callbacks;

    const objectUrlsRef = useRef<Set<string>>(new Set());

    const createTrackedObjectUrl = useCallback((file: File): string =>
    {
        const url = URL.createObjectURL(file);
        objectUrlsRef.current.add(url);
        return url;
    }, []);

    const revokeTrackedObjectUrl = useCallback((url: string | null | undefined): void =>
    {
        if(url && objectUrlsRef.current.has(url))
        {
            URL.revokeObjectURL(url);
            objectUrlsRef.current.delete(url);
        }
    }, []);

    useEffect(() =>
    {
        const urls = objectUrlsRef.current;
        return () =>
        {
            urls.forEach((url) => URL.revokeObjectURL(url));
            urls.clear();
        };
    }, []);

    // En modo edición, reutilizar las previews ya disponibles sin volver a consultar al backend
    useEffect(() =>
    {
        if(mode !== "edit" || !userIdentityId)
        {
            return;
        }

        // Con imágenes ya seleccionadas este efecto no vuelve a mandar: reemitir las del detalle
        // (inexistentes cuando el interviniente no las tenía) descartaría las recién aportadas.
        if(stateRef.current.idFrontFile || stateRef.current.idBackFile)
        {
            return;
        }

        const frontUrl = typeof initialFrontPreview === "string" ? initialFrontPreview : null;
        const backUrl = typeof initialBackPreview === "string" ? initialBackPreview : null;
        const hasPreviews = !!(frontUrl || backUrl);
        const hasDni = hasStoredIdDocument(initialIdDocument);
        // El interviniente puede tener el documento adjunto en BD aunque no se hayan podido
        // recuperar las URLs de preview. En ese caso también hay que pedir el DNI manualmente:
        // de lo contrario el formulario queda bloqueado sin ninguna vía para introducirlo.
        const hasDocuments = hasPreviews || hasStoredDocuments === true;

        // Esperar a que el controller cargue las URLs de adjuntos antes de cerrar la fase.
        if(!hasDni && !hasDocuments)
        {
            return;
        }

        // Borrador/interviniente con documento adjunto pero sin DNI: obligar captura manual + analyzeStatus.
        if(hasDocuments && !hasDni)
        {
            setState((prev) =>
            ({
                ...prev,
                idFrontPreview: frontUrl,
                idBackPreview: backUrl,
                phase: "dni_required",
                resolvedAction: null,
                identityStatus: null,
                statusMessage: "",
            }));

            callbacksRef.current.onDocumentProcessed(
                {success: false, idFrontPreview: frontUrl ?? undefined, idBackPreview: backUrl ?? undefined},
                frontUrl ?? "",
                backUrl ?? "",
            );
            return;
        }

        setState((prev) =>
        ({
            ...prev,
            idFrontPreview: frontUrl,
            idBackPreview: backUrl,
            phase: "flow_resolved",
            resolvedAction: null,
        }));

        callbacksRef.current.onDocumentProcessed({success: true}, frontUrl ?? "", backUrl ?? "");
    }, [mode, userIdentityId, initialFrontPreview, initialBackPreview, initialIdDocument, hasStoredDocuments]);

    const handleDocumentChange = useCallback((file: File | null, side: "front" | "back") =>
    {
        callbacksRef.current.onError("");

        const previousPreview = side === "front" ? stateRef.current.idFrontPreview : stateRef.current.idBackPreview;

        // En edición con renovación permitida (ACTIVE/OUTDATED/PENDING, o NEW caducado): solo actualizar el archivo y
        // notificar al controller sin resetear el ciclo ni disparar analyzeDocument (en edición no existe
        // el botón "Procesar documento", así que la rama de análisis no aplicaría).
        if(canRenewIdentityDocument)
        {
            const preview = file ? createTrackedObjectUrl(file) : null;
            if(preview)
            {
                revokeTrackedObjectUrl(previousPreview);
            }
            const current = stateRef.current;
            const frontPreviewStr = side === "front" ? (preview ?? current.idFrontPreview ?? "") : (current.idFrontPreview ?? "");
            const backPreviewStr = side === "back" ? (preview ?? current.idBackPreview ?? "") : (current.idBackPreview ?? "");
            const frontFile = side === "front" ? file : current.idFrontFile;
            const backFile = side === "back" ? file : current.idBackFile;

            setState((prev) => side === "front"
                ? {...prev, idFrontFile: file, idFrontPreview: preview ?? prev.idFrontPreview}
                : {...prev, idBackFile: file, idBackPreview: preview ?? prev.idBackPreview});

            callbacksRef.current.onDocumentProcessed(
            {
                success: true,
                idFrontFile: frontFile ?? undefined,
                idBackFile: backFile ?? undefined,
                idFrontPreview: frontPreviewStr || undefined,
                idBackPreview: backPreviewStr || undefined,
            },
            frontPreviewStr,
            backPreviewStr,
            );
            return;
        }

        revokeTrackedObjectUrl(previousPreview);
        const preview = file ? createTrackedObjectUrl(file) : null;
        setState((prev) =>
        {
            // Cambiar cualquiera de las imágenes reinicia el ciclo completo
            const reset =
            {
                phase: "idle" as const,
                statusMessage: "",
                resolvedAction: null,
                identityStatus: null,
                manualDniValue: "",
                manualDniError: "",
            };
            if(side === "front")
            {
                return {...prev, ...reset, idFrontFile: file, idFrontPreview: preview};
            }
            return {...prev, ...reset, idBackFile: file, idBackPreview: preview};
        });
    }, [canRenewIdentityDocument, createTrackedObjectUrl, revokeTrackedObjectUrl]);

    const handleProcessDocument = useCallback(async () =>
    {
        // Leer el estado actual de forma síncrona usando una ref temporal
        const currentState = stateRef.current;

        if(!currentState.idFrontFile || !currentState.idBackFile)
        {
            callbacksRef.current.onError("Debes subir ambas caras del documento de identidad.");
            return;
        }

        const idFrontPreview = currentState.idFrontPreview ?? createTrackedObjectUrl(currentState.idFrontFile);
        const idBackPreview = currentState.idBackPreview ?? createTrackedObjectUrl(currentState.idBackFile);

        setState((prev) => ({...prev, phase: "uploading", manualDniError: "", statusMessage: ""}));
        callbacksRef.current.onError("");
        callbacksRef.current.onServerWarning("");

        try
        {
            const formData = new FormData();
            formData.append("idFront", currentState.idFrontFile);
            formData.append("idBack", currentState.idBackFile);

            const apiResponse = await UserIdentitiesService.analyzeDocument(formData);
            const mapped = mapAnalyzeDocumentResponseToProcessingResult(apiResponse, idFrontPreview, idBackPreview);

            const result = mapped.ok ? mapped.result : mapped.partialResult;

            callbacksRef.current.onDocumentProcessed(
                {
                    ...result,
                    idFrontFile: currentState.idFrontFile,
                    idBackFile: currentState.idBackFile,
                },
                result.idFrontPreview ?? idFrontPreview,
                result.idBackPreview ?? idBackPreview,
            );
            setState((prev) =>
            ({
                ...prev,
                idFrontPreview: result.idFrontPreview ?? prev.idFrontPreview,
                idBackPreview: result.idBackPreview ?? prev.idBackPreview,
            }));

            if(!mapped.ok)
            {
                callbacksRef.current.onServerWarning("No se ha podido obtener la información del documento de manera automática. Por favor, insértelo manualmente.");
                setState((prev) => ({...prev, phase: "dni_required"}));
                return;
            }

            // DNI disponible: lanzar analyzeIdentityStatus
            setState((prev) => ({...prev, phase: "analyzing_status"}));
            const {action, status, warning, error} = await runAnalyzeStatus(result.documentNumber ?? "");

            callbacksRef.current.onServerWarning(warning);
            if(error)
            {
                // Error en analyzeStatus: el usuario puede reintentar procesando de nuevo
                callbacksRef.current.onError(error);
                setState((prev) =>
                ({
                    ...prev,
                    phase: "idle",
                    statusMessage: error,
                    resolvedAction: null,
                    identityStatus: null,
                }));
                return;
            }

            setState((prev) =>
            ({
                ...prev,
                phase: "flow_resolved",
                resolvedAction: action,
                identityStatus: status,
                statusMessage: "",
            }));
        }
        catch
        {
            // Error de red: volvemos a idle para que el usuario pueda reintentar
            const msg = "Error al procesar el documento. Inténtalo de nuevo.";
            callbacksRef.current.onError(msg);
            callbacksRef.current.onDocumentProcessed({success: false, idFrontPreview, idBackPreview}, idFrontPreview, idBackPreview);
            setState((prev) => ({...prev, phase: "idle", statusMessage: msg}));
        }
    }, [createTrackedObjectUrl]);

    const handleManualDniChange = useCallback((value: string) =>
    {
        setState((prev) => ({...prev, manualDniValue: value, manualDniError: ""}));
    }, []);

    const handleManualDniConfirm = useCallback(async () =>
    {
        const dni = stateRef.current.manualDniValue.trim().toUpperCase();
        if(!dni)
        {
            setState((prev) => ({...prev, manualDniError: "El número de documento es obligatorio."}));
            return;
        }

        callbacksRef.current.onDniConfirmed(dni);
        setState((prev) => ({...prev, phase: "analyzing_status", manualDniError: ""}));

        const {action, status, warning, error} = await runAnalyzeStatus(dni);

        callbacksRef.current.onServerWarning(warning);
        if(error)
        {
            // Error en analyzeStatus tras DNI manual: volvemos a dni_required para que reintente
            callbacksRef.current.onError(error);
            setState((prev) =>
            ({
                ...prev,
                phase: "dni_required",
                statusMessage: error,
                resolvedAction: null,
                identityStatus: null,
            }));
            return;
        }

        setState((prev) =>
        ({
            ...prev,
            phase: "flow_resolved",
            resolvedAction: action,
            identityStatus: status,
            statusMessage: "",
        }));
    }, []);

    // Ref al estado actual para acceder de forma síncrona dentro de los handlers
    const stateRef = useRef(state);
    stateRef.current = state;

    return{
        state,
        handleDocumentChange,
        handleProcessDocument,
        handleManualDniChange,
        handleManualDniConfirm,
    };
}